import { supabase } from '../../../services/supabase/client'
import type { Database, Json } from '../../../types/database.types'
import type { ReportType } from '../config/reportType'

/**
 * The body a report PDF uploads as. supabase-js wraps a Blob in a multipart
 * form, and Cloudflare (in front of Supabase Storage) rejects some of
 * Firefox's multipart uploads with a bare HTML 400 before Storage sees them
 * — the same PDF uploads from Chrome and iOS Safari. So Firefox sends the raw
 * bytes. Everyone else keeps the Blob: on iOS, a second in-page copy of a
 * large PDF is exactly the memory a 288-tree report can't spare (see
 * REPORT_PHOTO_BUDGET_BYTES). Firefox on iOS ("FxiOS") is WebKit, and stays
 * on the Blob path.
 */
export function pdfUploadBody(blob: Blob): Promise<Blob | ArrayBuffer> {
  const isGecko = typeof navigator !== 'undefined' && /\bFirefox\//.test(navigator.userAgent)
  return isGecko ? blob.arrayBuffer() : Promise.resolve(blob)
}

// Plain interface rather than deriving from Database['public']['Tables']['reports']['Row'].
// Supabase's recursive Json type causes TS2589 ("excessively deep") when Report
// is used inside Immer's WritableDraft in the Redux slice.
export interface Report {
  id: string
  job_id: string
  report_type: string
  version: number
  status: Database['public']['Enums']['report_status']
  summary: string | null
  overall_recommendations: string | null
  report_data: unknown
  metadata: unknown
  pdf_path: string | null
  pdf_size_bytes: number | null
  generated_by: string
  generated_at: string | null
  generation_started_at: string | null
  generation_completed_at: string | null
  generation_error: string | null
  template_id: string | null
  delivered_at: string | null
  delivered_by: string | null
  delivery_method: string | null
  read_at: string | null
  read_by: string | null
  created_at: string | null
  /** Embedded client engagement (Phase 6); present on job-list fetches */
  report_signoffs?: { id: string; signed_name: string; signed_at: string }[]
  report_feedback?: { count: number }[]
}

export type ReportStatus = Database['public']['Enums']['report_status']

export class ReportService {
  /**
   * Get all reports for a job, ordered by version descending
   */
  async getReportsByJobId(jobId: string): Promise<Report[]> {
    const { data, error } = await supabase
      .from('reports')
      .select('*, report_signoffs ( id, signed_name, signed_at ), report_feedback ( count )')
      .eq('job_id', jobId)
      .order('version', { ascending: false })

    if (error) throw error
    return (data ?? []) as unknown as Report[]
  }

  /**
   * Get a single report by ID
   */
  async getReport(id: string): Promise<Report | null> {
    const { data, error } = await supabase.from('reports').select('*').eq('id', id).single()
    if (error) throw error
    return data
  }

  /**
   * Upload a generated PDF blob to Supabase Storage.
   * Returns the storage path and file size in bytes.
   */
  async uploadPdf(
    blob: Blob,
    jobId: string,
    version: number,
    reportType: ReportType = 'full_report'
  ): Promise<{ path: string; size: number }> {
    // Path format: {jobId}/{jobId}_v{version}.pdf for full reports (unchanged
    // for existing objects); {jobId}/report-{type}-v{version}.pdf for the
    // findings variant so two variants at one version never overwrite each
    // other (008 R7). The leading folder segment is the job ID — required for
    // the storage RLS policy to extract and validate it via foldername(name)[1].
    const fileName =
      reportType === 'full_report'
        ? `${jobId}/${jobId}_v${version}.pdf`
        : `${jobId}/report-${reportType}-v${version}.pdf`

    const { data, error } = await supabase.storage
      .from('reports')
      .upload(fileName, await pdfUploadBody(blob), {
        contentType: 'application/pdf',
        upsert: true,
      })

    if (error) throw new Error(`Failed to upload PDF: ${error.message}`)

    return { path: data.path, size: blob.size }
  }

  /**
   * Create or update the report record in the database.
   * Upserts on (job_id, report_type, version) — versions are per variant
   * since 008 — so re-generating a version overwrites it.
   */
  async saveReportRecord(
    jobId: string,
    version: number,
    pdfPath: string,
    pdfSize: number,
    generatedBy: string,
    metadata?: Record<string, unknown> | null,
    reportType: ReportType = 'full_report'
  ): Promise<Report> {
    const { data, error } = await supabase
      .from('reports')
      .upsert(
        {
          job_id: jobId,
          report_type: reportType,
          version,
          status: 'approved' as ReportStatus,
          pdf_path: pdfPath,
          pdf_size_bytes: pdfSize,
          generated_by: generatedBy,
          generated_at: new Date().toISOString(),
          generation_completed_at: new Date().toISOString(),
          generation_error: null,
          // Immutable generation-time snapshot (003): which brand/layout made
          // this PDF. Later preset edits never touch it.
          metadata: (metadata ?? null) as Json,
        },
        { onConflict: 'job_id,report_type,version' }
      )
      .select()
      .single()

    if (error) throw new Error(`Failed to save report record: ${error.message}`)
    return data
  }

  /**
   * Create in-app notifications for all client users associated with the job.
   * Failures are caught silently — notifications are best-effort and should
   * not block the user from receiving their PDF.
   */
  async createReportNotifications(jobId: string, reportId: string): Promise<void> {
    try {
      const { data: job } = await supabase
        .from('jobs')
        .select('client_org_id, org_id')
        .eq('id', jobId)
        .single()

      if (!job) return

      // Grants are pair-scoped (5d): only contacts this org granted access
      // for this client company receive the notification
      const { data: clientGrants } = await supabase
        .from('org_clients')
        .select('user_id')
        .eq('client_org_id', job.client_org_id)
        .eq('org_id', job.org_id)

      if (!clientGrants || clientGrants.length === 0) return

      const notifications = clientGrants.map((cu) => ({
        recipient_id: cu.user_id,
        notification_type: 'report_ready' as const,
        title: 'Report Ready',
        message: 'Your tree inspection report has been generated and is ready for review.',
        related_entity_type: 'report',
        related_entity_id: reportId,
      }))

      await supabase.from('notifications').insert(notifications)
    } catch {
      // Best-effort — do not surface notification failures to the user
    }
  }

  /**
   * Record delivery of a report to the client (RPT-04).
   * Writes delivered_at / delivered_by / delivery_method on the report row.
   */
  async markDelivered(reportId: string, method: 'email' | 'portal' | 'manual'): Promise<Report> {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) throw new Error('Not authenticated')

    // Email delivery actually sends the report_ready email to every client
    // user of the job (server-side fan-out). Only record delivery if the
    // send succeeded.
    if (method === 'email') {
      // invoke() types its error channel as `any`; pin the response shape
      const { data: result, error: fnError } = (await supabase.functions.invoke(
        'send-notification-email',
        { body: { reportId } }
      )) as { data: { sent?: number } | null; error: Error | null }
      if (fnError) {
        throw new Error(`Failed to send delivery email: ${fnError.message}`)
      }
      if ((result?.sent ?? 0) === 0) {
        throw new Error('No delivery emails could be sent')
      }
    }

    const { data, error } = await supabase
      .from('reports')
      .update({
        delivered_at: new Date().toISOString(),
        delivered_by: user.id,
        delivery_method: method,
        status: 'delivered' as ReportStatus,
      })
      .eq('id', reportId)
      .select()
      .single()

    if (error) throw new Error(`Failed to record delivery: ${error.message}`)

    // Delivery is what makes the report visible in the portal (Phase 6), so
    // this is the moment clients get their in-app report_ready notification.
    // Manual delivery happens outside the app — no notification.
    if (method !== 'manual') {
      void this.createReportNotifications(data.job_id, reportId)
    }

    return data
  }

  /**
   * Get a short-lived signed download URL for a report PDF
   */
  async getDownloadUrl(pdfPath: string, expiresInSeconds = 3600): Promise<string> {
    const { data, error } = await supabase.storage
      .from('reports')
      .createSignedUrl(pdfPath, expiresInSeconds)

    if (error) throw error
    if (!data?.signedUrl) throw new Error('Failed to create signed URL')

    return data.signedUrl
  }

  /**
   * Delete a report record (does not remove the PDF from storage)
   */
  async deleteReport(id: string): Promise<void> {
    const { error } = await supabase.from('reports').delete().eq('id', id)
    if (error) throw error
  }
}

export const reportService = new ReportService()
